export interface Shot {
  src: string;
  alt: string;
  caption?: string;
}

export interface Project {
  id: string;
  name: string;
  /** "showcase" renders framed carousels, "fan" renders three fanned phones. */
  kind: "showcase" | "fan";
  /** One plain line under the project name. */
  kindLabel: string;
  url?: string;
  stores?: { play?: string; apple?: string };
  /** The screen the redesign replaced; shown next to the carousel. */
  before?: Shot;
  extraLink?: { label: string; href: string };
  /** Dashboard / web screens shown in a browser-framed carousel next to the phone. */
  desktopImages?: Shot[];
  blurb: string;
  highlights: string[];
  stack: string[];
  images: Shot[];
}

export const projects: Project[] = [
  {
    id: "ireserved",
    name: "iReserved App",
    kind: "showcase",
    kindLabel: "Mobile app, designed and built by me",
    stores: {
      play: "https://play.google.com/store/apps/details?id=com.ireserved.booking",
      apple: "https://apps.apple.com/ae/app/ireserved-make-life-easier/id6739726864",
    },
    blurb:
      "A Dubai hospitality booking app for fine dining, beach clubs, lounges and events, with a bottle vault, smart wallet and AIR, an AI concierge. I designed the new interface and built the app.",
    highlights: [
      "Redesigned from a flat light theme to a night-out dark interface",
      "AIR, an AI concierge that plans the evening from a one-line brief",
      "Membership tiers, bottle vault, smart wallet and payment history",
      "Live on the App Store and Google Play",
    ],
    stack: ["React Native", "TypeScript", "Redux", "Node.js", "Figma"],
    before: {
      src: "/images/ireserved-before.webp",
      alt: "The previous iReserved home screen: a light lavender header with an illustration and six outlined category icons",
      caption: "Before",
    },
    images: [
      { src: "/images/ireserved-home.webp", alt: "New iReserved home: Dubai skyline header, search, category tiles and a spin-the-wheel prompt", caption: "Home" },
      { src: "/images/ireserved-dining.webp", alt: "Fine dining list with filters for nearby, rated 4+ and open now", caption: "Discover" },
      { src: "/images/ireserved-profile.webp", alt: "Profile with the Explorer membership card, bottle vault and smart wallet", caption: "Profile" },
      { src: "/images/ireserved-air.webp", alt: "AIR, the AI concierge, asking what are we doing tonight with idea chips", caption: "AIR concierge" },
    ],
  },
  {
    id: "service-crm",
    name: "Service CRM",
    kind: "showcase",
    kindLabel: "Multi-tenant SaaS web platform, client name withheld",
    blurb:
      "A CRM for UAE service teams, one workspace from first contact to final invoice: leads and deals boards, quotations, proformas and VAT-ready invoices in AED, accounts, projects and reporting.",
    highlights: [
      "Kanban pipelines for leads and deals with owners, priorities and stages",
      "Quotation to proforma to invoice flow with payment tracking",
      "Income, expense and receivables dashboards with audit log",
      "Multi-tenant workspaces with role-based users",
    ],
    stack: ["React", "TypeScript", "Vite", "Node.js", "PostgreSQL"],
    desktopImages: [
      { src: "/images/crm-dashboard.webp", alt: "Workspace dashboard with deals won, active leads, win rate, revenue trend and activity breakdown", caption: "Dashboard" },
      { src: "/images/crm-leads.webp", alt: "Leads kanban board with new, contacted, follow-up and qualified columns", caption: "Leads" },
      { src: "/images/crm-deals.webp", alt: "Deals pipeline from open to proposal sent and negotiation", caption: "Deals" },
      { src: "/images/crm-invoices.webp", alt: "Invoices list with totals, paid, overdue and outstanding amounts in AED", caption: "Invoices" },
      { src: "/images/crm-accounts.webp", alt: "Accounts dashboard with income versus expense trend and recent transactions", caption: "Accounts" },
      { src: "/images/crm-reports.webp", alt: "Reports with sales summary, invoiced versus collected and MoM growth", caption: "Reports" },
    ],
    images: [],
  },
  {
    id: "whakaaro",
    name: "Whakaaro Food Delivery",
    kind: "showcase",
    kindLabel: "Mobile apps and management dashboards",
    stores: {
      apple: "https://apps.apple.com/om/app/whakaaro/id1587840725",
      play: "https://play.google.com/store/apps/details?id=com.hedoneservices.foodappp",
    },
    blurb:
      "A multi-role food delivery platform with 50k+ downloads: three mobile apps for customers, restaurants and riders, and four dashboards, with role-based access, real-time tracking and payment gateway integration.",
    highlights: [
      "Real-time order tracking across every role",
      "Nearby-restaurant and cross-restaurant dish search",
      "Branch dashboards for settlements, offers, banners and push notifications",
      "Payments, promo codes and rider bonus schemes",
    ],
    stack: ["Node.js", "MongoDB", "Express", "React", "React Native", "socket.io", "Redux", "AWS", "Firebase"],
    desktopImages: [
      { src: "/images/whakaaro-settlement.webp", alt: "Branch dashboard: delivery settlement with orders delivered, floating cash and bonus breakdown", caption: "Delivery settlement" },
      { src: "/images/whakaaro-offers.webp", alt: "Branch dashboard: offer builder with live preview and colour pickers", caption: "Offers" },
      { src: "/images/whakaaro-settings.webp", alt: "Branch settings: branch status, push notifications, popup banners and rider bonus types", caption: "Branch settings" },
    ],
    images: [
      { src: "/images/whakaaro-home.webp", alt: "Customer app home with delivery address, promo banner, offers and nearby restaurants", caption: "Home" },
      { src: "/images/whakaaro-menu.webp", alt: "Restaurant menu with must-try dishes and add buttons", caption: "Menu" },
      { src: "/images/whakaaro-checkout.webp", alt: "Checkout with delivery note, offers, payment details and confirm order", caption: "Checkout" },
      { src: "/images/whakaaro-placed.webp", alt: "Order placed successfully screen with rider illustration and bill summary", caption: "Order placed" },
    ],
  },
  {
    id: "incuse",
    name: "Incuse HRMS",
    kind: "showcase",
    kindLabel: "HR management web app",
    url: "https://flipkart.incusesb.com/",
    blurb:
      "An employee management web app with roles and permissions, attendance, employee records and medicare management, built on React and Material UI.",
    highlights: [
      "Role and permission based access",
      "Attendance, overtime and shift tracking",
      "Letters, performance and rewards modules",
    ],
    stack: ["React", "Redux", "Material UI", "AWS"],
    desktopImages: [
      { src: "/images/incuse5.webp", alt: "Incuse HRMS home with modules for attendance, performance, letters, training and rewards", caption: "Modules" },
      { src: "/images/incuse3.webp", alt: "Incuse HRMS attendance table filtered by branch, department and employee", caption: "Attendance" },
      { src: "/images/incuse6.webp", alt: "Incuse HRMS employee detail showing presenteeism and overtime hours", caption: "Employee detail" },
    ],
    images: [],
  },
  {
    id: "myfumma",
    name: "myFumma",
    kind: "fan",
    kindLabel: "Association management app",
    url: "https://play.google.com/store/apps/details?id=com.backb.myfumma",
    extraLink: { label: "Design walkthrough", href: "https://www.youtube.com/watch?v=qMZRbQodYKY&ab_channel=BackBSolutions" },
    blurb:
      "An association management app: member profiles and points, bulk messaging to every member, event attendance by QR code, and fair in-app elections.",
    highlights: ["Digital membership card with points", "Bulk SMS to members", "QR attendance and voting"],
    stack: ["PHP", "MySQL", "HTML", "CSS", "Bootstrap", "Android Studio", "Xcode"],
    images: [
      { src: "/images/fumma4.png", alt: "myFumma dashboard with a gold digital membership card and points balance" },
      { src: "/images/fumma5.png", alt: "myFumma home grid with profile, meetings, points, voting and members" },
      { src: "/images/fumma3.png", alt: "myFumma member status screen with district, designation and renewal date" },
    ],
  },
  {
    id: "sindibad",
    name: "Sindibad",
    kind: "fan",
    kindLabel: "E-commerce for web and mobile",
    blurb:
      "A MERN-stack e-commerce product for web and mobile, shipped to the Play Store and App Store: brand storefronts, product variants, cart, coupons, wallet top-ups and order tracking.",
    highlights: ["Product variants and offers", "Cart, coupons and wallet", "Order history and tracking"],
    stack: ["React Native", "React", "Node.js", "MongoDB", "Redux"],
    images: [
      { src: "/images/sindibad2.jpeg", alt: "Sindibad storefront with flash sale banner, brand tiles and popular products" },
      { src: "/images/sindibad3.jpeg", alt: "Sindibad product page for an iPhone with colour variants and add to cart" },
      { src: "/images/sindibad9.jpeg", alt: "Sindibad offers screen with coupon cards" },
    ],
  },
];
